import React from "react";
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import "../../lib/maplibre-worker";
import {
  normaliseBearing,
  type StartSummary,
  type WhereToStartHill,
  type WhereToStartMode,
  type WhereToStartRegionMarker,
  type WhereToStartRemaining,
} from "@atlas/shared";
import { ScanSearch } from "lucide-react";
import { MAP_COLORS } from "./map-colors";

// Where to start map (spec 31 / ATL-146).
//
// Remaining hills (gold: filled on the first selected list, outline on any
// other), collected hills (small, muted spruce), start chips and region
// bubbles as HTML markers, and for a picked start the reach circle, the lobe
// at the current bearing, the runner-up lobe and the drag handle.
//
// Positioning (DECISIONS.md): nothing is ever drawn between hills or from the
// start to a hill. The lobe is an area, not a line.
//
// Colours: MapLibre paint can't read CSS variables, so layer colours come from
// MAP_COLORS (mirrors of the tokens). HTML markers use the tokens directly.

const MAP_STYLE = "https://tiles.openfreemap.org/styles/dark";
const { gold: GOLD, spruce: SPRUCE, textOnGold: HALO, textOnGrey: LIGHT } = MAP_COLORS;
const CLEAR = "rgba(0,0,0,0)";

/** Region bubbles below this zoom, individual starts at or above it. */
const REGION_ZOOM = 8;
const KEY_STEP_DEG = 22.5;
/** With an origin set, the map focuses on this radius around it. */
export const DEFAULT_RADIUS_KM = 7.5;
export const AREA_LABEL = "This area";
export interface WtsOrigin {
  lat: number;
  lng: number;
  radiusKm: number;
}
const M_PER_DEG = 111_320;

type Pt = [number, number]; // lng, lat

export function destination(lng: number, lat: number, distM: number, bearingDeg: number): Pt {
  const b = (bearingDeg * Math.PI) / 180;
  return [
    lng + (distM * Math.sin(b)) / (M_PER_DEG * Math.cos((lat * Math.PI) / 180)),
    lat + (distM * Math.cos(b)) / M_PER_DEG,
  ];
}

function circlePolygon(centre: Pt, radiusM: number, n = 64) {
  const ring: Pt[] = [];
  for (let i = 0; i <= n; i++) ring.push(destination(centre[0], centre[1], radiusM, (i / n) * 360));
  return { type: "Polygon" as const, coordinates: [ring] };
}

export type Bounds = [number, number, number, number]; // w, s, e, n

/** Bounding box of a circle of `radiusM` around a point. */
export function reachBounds(lat: number, lng: number, radiusM: number): Bounds {
  const n = destination(lng, lat, radiusM, 0);
  const e = destination(lng, lat, radiusM, 90);
  const s = destination(lng, lat, radiusM, 180);
  const w = destination(lng, lat, radiusM, 270);
  return [w[0], s[1], e[0], n[1]];
}

export function boundsOf(points: Array<[number, number]>): Bounds | null {
  if (points.length === 0) return null;
  let w = Infinity;
  let s = Infinity;
  let e = -Infinity;
  let n = -Infinity;
  for (const [lng, lat] of points) {
    if (lng < w) w = lng;
    if (lng > e) e = lng;
    if (lat < s) s = lat;
    if (lat > n) n = lat;
  }
  return [w, s, e, n];
}

export function haversineM(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const rad = Math.PI / 180;
  const dLat = (bLat - aLat) * rad;
  const dLng = (bLng - aLng) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(aLat * rad) * Math.cos(bLat * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * 6_371_000 * Math.asin(Math.min(1, Math.sqrt(h)));
}

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
}

type FC = { type: "FeatureCollection"; features: unknown[] };
const EMPTY: FC = { type: "FeatureCollection", features: [] };

const CSS = `
.wts-chip{display:flex;align-items:center;gap:5px;height:26px;min-width:26px;box-sizing:border-box;padding:0 8px;border-radius:7px;
  background:var(--surface-card);border:1px solid var(--border-strong);color:var(--text-primary);cursor:pointer;
  font:700 13px var(--font-display);box-shadow:0 2px 6px rgba(0,0,0,.5);justify-content:center}
.wts-chip:hover{background:var(--surface-raised)}
.wts-chip:focus-visible,.wts-handle:focus-visible{outline:2px solid var(--accent);outline-offset:2px}
.wts-chip .g{display:flex;align-items:center;font:700 11px var(--font-display);opacity:.85}
.wts-chip.best{height:34px;padding:0 11px;border-radius:8px;border-color:transparent;background:var(--accent);color:var(--text-on-gold);font-size:16px;z-index:2}
.wts-chip.best:hover{background:var(--accent-hover)}
.wts-chip.best .g{font-size:12px}
.wts-bub{display:flex;flex-direction:column;align-items:center;gap:6px;border:0;background:none;padding:0;cursor:pointer;font-family:var(--font-sans)}
.wts-bub .pill{display:flex;align-items:center;gap:6px;height:34px;padding:0 12px;border-radius:10px;background:var(--surface-card);
  border:1px solid var(--border-strong);color:var(--text-primary);font:700 16px var(--font-display);box-shadow:0 4px 14px rgba(0,0,0,.5)}
.wts-bub .pill small{font:600 12px var(--font-sans);color:var(--text-secondary)}
.wts-bub.best .pill{background:var(--accent);border-color:transparent;color:var(--text-on-gold)}
.wts-bub.best .pill small{color:var(--text-on-gold)}
.wts-bub .cap{font:500 12px var(--font-sans);color:var(--text-primary);text-shadow:0 1px 3px #000}
.wts-handle{position:relative;width:34px;height:34px;border-radius:50%;background:var(--bg-base);border:2px solid var(--accent);
  box-shadow:0 2px 10px rgba(0,0,0,.6);display:flex;align-items:center;justify-content:center;cursor:grab;padding:0;color:var(--text-accent);touch-action:none}
.wts-handle:active{cursor:grabbing}
.wts-hint{position:absolute;left:40px;top:22px;white-space:nowrap;pointer-events:none;font:600 11px var(--font-sans);color:var(--text-accent);text-shadow:0 1px 3px #000}
.wts-origin{width:14px;height:14px;border-radius:50%;background:var(--status-info);border:2px solid var(--bg-base);box-shadow:0 0 0 2px var(--status-info)}
.wts-pop{font:600 12px var(--font-sans);color:var(--ink-950)}
.wts-map .maplibregl-ctrl-attrib{background:var(--surface-card);color:var(--text-secondary);border:1px solid var(--border-subtle);border-radius:8px;font-size:11px}
.wts-map .maplibregl-ctrl-attrib.maplibregl-compact:not([open]){background:transparent;border-color:transparent}
.wts-map .maplibregl-ctrl-attrib a{color:var(--text-secondary)}
.wts-map .maplibregl-ctrl-attrib-button{filter:invert(1) opacity(.7)}
.wts-map .maplibregl-ctrl-logo,.wts-map .maplibregl-ctrl-bottom-right .maplibregl-ctrl:has(img){display:none}
`;

function ensureCss() {
  if (typeof document === "undefined" || document.getElementById("wts-map-css")) return;
  const s = document.createElement("style");
  s.id = "wts-map-css";
  s.textContent = CSS;
  document.head.appendChild(s);
}

// Static SVG strings only (no user data goes through innerHTML).
const SVG_OPEN =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">';
const TRAIN_SVG = `${SVG_OPEN}<path d="M8 3.1V7a4 4 0 0 0 8 0V3.1"/><path d="m9 15-1-1"/><path d="m15 15 1-1"/><path d="M9 19c-2.8 0-5-2.2-5-5v-4a8 8 0 0 1 16 0v4c0 2.8-2.2 5-5 5Z"/><path d="m8 19-2 3"/><path d="m16 19 2 3"/></svg>`;
const SWING_SVG = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 4 4 8l4 4"/><path d="M4 8h9a7 7 0 0 1 0 14"/><path d="m16 18 4 4"/></svg>`;

function chipEl(start: StartSummary, best: boolean, count: number): HTMLButtonElement {
  const el = document.createElement("button");
  el.type = "button";
  el.className = `wts-chip${best ? " best" : ""}`;
  el.setAttribute("aria-label", `${start.name}, ${count} ${count === 1 ? "hill" : "hills"}`);
  const g = document.createElement("span");
  g.className = "g";
  g.setAttribute("aria-hidden", "true");
  if (start.kind === "station") g.innerHTML = TRAIN_SVG;
  else g.textContent = "P";
  const n = document.createElement("span");
  n.className = "n";
  n.setAttribute("aria-hidden", "true");
  n.textContent = String(count);
  el.append(g, n);
  return el;
}

export interface WtsPicked {
  start: StartSummary;
  /** Lobe radius (r) or centred radius (loop / 4). */
  radiusM: number;
  mode: WhereToStartMode;
  /** Live bearing; null in centred mode. */
  bearing: number | null;
  bestBearing: number | null;
  runnerUpBearing: number | null;
  /** Remaining hills within reach; `inside` already reflects `bearing`. */
  hills: WhereToStartHill[];
  insideCount: number;
}

export interface WhereToStartMapProps {
  remaining: WhereToStartRemaining | undefined;
  starts: StartSummary[];
  regions: WhereToStartRegionMarker[];
  origin: WtsOrigin | null;
  picked: WtsPicked | null;
  fit: { key: string; bounds: Bounds | null };
  onPickStart: (id: string) => void;
  /** "Search this area": the view centre and half its shorter side. */
  onSearchArea: (area: { lat: number; lng: number; radiusKm: number }) => void;
  /** Live while dragging (not yet in the URL). */
  onBearingChange: (deg: number) => void;
  /** Drag ended or a key was pressed: persist and refetch. */
  onBearingCommit: (deg: number) => void;
  children?: React.ReactNode;
  style?: React.CSSProperties;
}

export function WhereToStartMap(props: WhereToStartMapProps) {
  const { remaining, starts, regions, origin, picked, fit, style, children } = props;
  const containerRef = React.useRef<HTMLDivElement>(null);
  const mapRef = React.useRef<maplibregl.Map | null>(null);
  const [ready, setReady] = React.useState(false);
  const [zoom, setZoom] = React.useState(7);
  const [view, setView] = React.useState<{ lat: number; lng: number; zoom: number } | null>(null);
  const [dragging, setDragging] = React.useState(false);
  const baseZoomRef = React.useRef<number | null>(null);
  const fitPendingRef = React.useRef(false);

  const cb = React.useRef(props);
  cb.current = props;

  const handleRef = React.useRef<maplibregl.Marker | null>(null);
  const handleElRef = React.useRef<HTMLButtonElement | null>(null);
  const hintRef = React.useRef<HTMLElement | null>(null);
  const draggedRef = React.useRef(false);
  const pickedCountRef = React.useRef<HTMLElement | null>(null);

  // --- map init ---------------------------------------------------------
  React.useEffect(() => {
    ensureCss();
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAP_STYLE,
      center: [-3.1, 54.5],
      zoom: 6,
      attributionControl: { compact: true },
      fadeDuration: 0,
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    map.on("zoomend", () => setZoom(map.getZoom()));
    map.on("moveend", () => {
      const c = map.getCenter();
      const z = map.getZoom();
      if (fitPendingRef.current) {
        fitPendingRef.current = false;
        baseZoomRef.current = z;
      }
      setView({ lat: c.lat, lng: c.lng, zoom: z });
    });
    // MapLibre opens compact attribution expanded (a bright pill on the dark
    // map). Start it collapsed; the (i) button still opens the credits.
    map.once("idle", () => {
      const attrib = map.getContainer().querySelector(".maplibregl-ctrl-attrib");
      attrib?.classList.remove("maplibregl-compact-show");
      // MapLibre 6 renders the control as <details open>; the class alone
      // does not close it.
      if (attrib instanceof HTMLDetailsElement) attrib.open = false;
    });
    map.on("load", () => {
      for (const layer of map.getStyle().layers ?? []) {
        if ("source-layer" in layer && layer["source-layer"] === "aeroway") {
          map.setLayoutProperty(layer.id, "visibility", "none");
        }
      }
      for (const id of ["remaining", "collected", "reach-hills", "reach", "lobe", "runner", "berth"]) {
        map.addSource(id, { type: "geojson", data: EMPTY as never });
      }
      // Areas first, so hills draw over them.
      map.addLayer({
        id: "reach-line",
        type: "line",
        source: "reach",
        layout: { "line-cap": "round" },
        paint: { "line-color": GOLD, "line-width": 1.2, "line-opacity": 0.3, "line-dasharray": [0.5, 2.5] },
      });
      map.addLayer({
        id: "berth-line",
        type: "line",
        source: "berth",
        layout: { "line-cap": "round" },
        paint: { "line-color": MAP_COLORS.grey, "line-width": 1.4, "line-opacity": 0.8, "line-dasharray": [0.5, 2.5] },
      });
      map.addLayer({
        id: "runner-line",
        type: "line",
        source: "runner",
        paint: { "line-color": LIGHT, "line-width": 1.2, "line-opacity": 0.35, "line-dasharray": [3, 3] },
      });
      map.addLayer({
        id: "lobe-fill",
        type: "fill",
        source: "lobe",
        paint: { "fill-color": GOLD, "fill-opacity": 0.08 },
      });
      map.addLayer({
        id: "lobe-line",
        type: "line",
        source: "lobe",
        paint: { "line-color": GOLD, "line-width": 1.8, "line-opacity": 0.9, "line-dasharray": [3, 2.5] },
      });

      const radius = ["interpolate", ["linear"], ["zoom"], 5, 2.5, 9, 4, 13, 6.5] as never;
      map.addLayer({
        id: "collected",
        type: "circle",
        source: "collected",
        paint: { "circle-color": SPRUCE, "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 1.8, 10, 3.5, 13, 5] as never, "circle-opacity": 0.55 },
      });
      map.addLayer({
        id: "remaining-other",
        type: "circle",
        source: "remaining",
        filter: ["==", ["get", "top"], false],
        paint: {
          "circle-color": CLEAR,
          "circle-radius": radius,
          "circle-stroke-width": 1.8,
          "circle-stroke-color": GOLD,
          "circle-stroke-opacity": 0.85,
        },
      });
      map.addLayer({
        id: "remaining-top",
        type: "circle",
        source: "remaining",
        filter: ["==", ["get", "top"], true],
        paint: { "circle-color": GOLD, "circle-radius": radius, "circle-opacity": 0.85 },
      });

      // Hills within reach of the picked start, drawn over the base layers.
      map.addLayer({
        id: "reach-halo",
        type: "circle",
        source: "reach-hills",
        filter: ["==", ["get", "inside"], true],
        paint: { "circle-color": HALO, "circle-radius": 10, "circle-opacity": 0.8 },
      });
      map.addLayer({
        id: "reach-out",
        type: "circle",
        source: "reach-hills",
        filter: ["==", ["get", "inside"], false],
        paint: {
          "circle-color": ["case", ["get", "top"], GOLD, CLEAR] as never,
          "circle-radius": 5,
          "circle-stroke-width": 1.8,
          "circle-stroke-color": GOLD,
          "circle-opacity": 0.4,
          "circle-stroke-opacity": 0.4,
        },
      });
      map.addLayer({
        id: "reach-in",
        type: "circle",
        source: "reach-hills",
        filter: ["==", ["get", "inside"], true],
        paint: {
          "circle-color": ["case", ["get", "top"], GOLD, CLEAR] as never,
          "circle-radius": 6.5,
          "circle-stroke-width": 2.4,
          "circle-stroke-color": GOLD,
        },
      });
      map.addLayer({
        id: "reach-label",
        type: "symbol",
        source: "reach-hills",
        filter: ["==", ["get", "inside"], true],
        layout: {
          "text-field": ["get", "name"],
          "text-font": ["Noto Sans Bold"],
          "text-size": 11,
          "text-anchor": "left",
          "text-offset": [0.9, 0],
          "text-optional": true,
        },
        paint: { "text-color": LIGHT, "text-halo-color": "rgba(9,12,16,0.9)", "text-halo-width": 1.5 },
      });

      const popup = new maplibregl.Popup({ closeButton: false, offset: 10 });
      const hover = ["remaining-top", "remaining-other", "reach-in", "reach-out"];
      for (const id of hover) {
        map.on("mouseenter", id, () => (map.getCanvas().style.cursor = "pointer"));
        map.on("mouseleave", id, () => (map.getCanvas().style.cursor = ""));
        map.on("click", id, (e) => {
          const f = e.features?.[0];
          if (!f || f.geometry.type !== "Point") return;
          const p = f.properties as { name?: string; elev?: number };
          const div = document.createElement("div");
          div.className = "wts-pop";
          div.textContent = `${p.name ?? "Hill"}${p.elev ? ` · ${Math.round(Number(p.elev)).toLocaleString("en-GB")} m` : ""}`;
          popup.setLngLat(f.geometry.coordinates as Pt).setDOMContent(div).addTo(map);
        });
      }
      setZoom(map.getZoom());
      setReady(true);
    });

    const ro = new ResizeObserver(() => map.resize());
    ro.observe(containerRef.current);
    return () => {
      ro.disconnect();
      map.remove();
      mapRef.current = null;
      setReady(false);
    };
  }, []);

  const setData = (id: string, data: FC) => {
    (mapRef.current?.getSource(id) as maplibregl.GeoJSONSource | undefined)?.setData(data as never);
  };

  // --- remaining + collected hills --------------------------------------
  React.useEffect(() => {
    if (!ready) return;
    const rem: unknown[] = [];
    const col: unknown[] = [];
    const o = origin;
    for (const p of remaining?.peaks ?? []) {
      if (o && haversineM(o.lat, o.lng, p.lat, p.lng) > o.radiusKm * 1000) continue;
      const f = {
        type: "Feature",
        geometry: { type: "Point", coordinates: [p.lng, p.lat] },
        properties: { name: p.name, elev: p.elevationM, top: p.c.includes(0) },
      };
      (p.collected ? col : rem).push(f);
    }
    setData("remaining", { type: "FeatureCollection", features: rem });
    setData("collected", { type: "FeatureCollection", features: col });
  }, [ready, remaining, origin?.lat, origin?.lng, origin?.radiusKm]);

  // Berth circle round the origin.
  React.useEffect(() => {
    if (!ready) return;
    setData("berth", origin ? { type: "FeatureCollection", features: [{ type: "Feature", geometry: circlePolygon([origin.lng, origin.lat], origin.radiusKm * 1000, 96), properties: {} }] } : EMPTY);
  }, [ready, origin?.lat, origin?.lng, origin?.radiusKm]);

  // Fade the base layers once a start is picked; the reach layers take over.
  const pickedId = picked?.start.id ?? null;
  React.useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    const dim = pickedId != null;
    map.setPaintProperty("remaining-top", "circle-opacity", dim ? 0.3 : 0.85);
    map.setPaintProperty("remaining-other", "circle-stroke-opacity", dim ? 0.3 : 0.85);
    map.setPaintProperty("collected", "circle-opacity", dim ? 0.35 : 0.55);
  }, [ready, pickedId]);

  // --- picked start: hills in reach -------------------------------------
  const pickedHills = picked?.hills;
  React.useEffect(() => {
    if (!ready) return;
    const topIds = new Set((remaining?.peaks ?? []).filter((p) => p.c.includes(0)).map((p) => p.id));
    setData("reach-hills", {
      type: "FeatureCollection",
      features: (pickedHills ?? []).map((h) => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: [h.lng, h.lat] },
        properties: { name: h.name, elev: h.elevationM, inside: h.inside, top: topIds.has(h.id) || h.c.includes(0) },
      })),
    });
  }, [ready, pickedHills, remaining]);

  // --- picked start: areas and handle -----------------------------------
  const pLat = picked?.start.lat;
  const pLng = picked?.start.lng;
  const pRadius = picked?.radiusM;
  const pMode = picked?.mode;
  const pBearing = picked?.bearing ?? null;
  const pRunner = picked?.runnerUpBearing ?? null;

  // Geometry currently on screen, so a change can tween from it.
  const geoRef = React.useRef<{ key: string; r: number; off: number; reach: number; bearing: number } | null>(null);
  const tweenRef = React.useRef(0);
  const draggingRef = React.useRef(false);

  React.useEffect(() => {
    if (!ready) return;
    cancelAnimationFrame(tweenRef.current);
    if (pLat == null || pLng == null || pRadius == null || !pMode) {
      geoRef.current = null;
      setData("reach", EMPTY);
      setData("lobe", EMPTY);
      setData("runner", EMPTY);
      return;
    }
    const start: Pt = [pLng, pLat];
    const feat = (geometry: unknown) => ({ type: "FeatureCollection" as const, features: [{ type: "Feature", geometry, properties: {} }] });
    const lobe = pMode === "lobe";
    const key = `${pickedId}`;
    const prev = geoRef.current;
    const target = {
      key,
      r: pRadius,
      off: lobe ? pRadius : 0,
      reach: lobe ? 2 * pRadius : pRadius,
      bearing: pBearing ?? prev?.bearing ?? 0,
    };

    // finalDraw is the exact geometry for the mode; frame is a tween step.
    const draw = (g: { r: number; off: number; reach: number; bearing: number }, final: boolean) => {
      const centre = destination(pLng, pLat, g.off, g.bearing);
      setData("lobe", feat(circlePolygon(centre, g.r)));
      if (final && !lobe) setData("reach", EMPTY);
      else setData("reach", feat(circlePolygon(start, g.reach)));
      if (lobe && pRunner != null) {
        setData("runner", feat(circlePolygon(destination(pLng, pLat, g.off, pRunner), g.r)));
      } else setData("runner", EMPTY);
      if (lobe) handleRef.current?.setLngLat(destination(pLng, pLat, 2 * g.off, g.bearing));
    };

    const same =
      prev != null &&
      prev.key === key &&
      Math.abs(prev.r - target.r) < 0.5 &&
      Math.abs(prev.off - target.off) < 0.5 &&
      Math.abs(prev.reach - target.reach) < 0.5 &&
      Math.abs(prev.bearing - target.bearing) < 0.01;
    if (same || !prev || prev.key !== key || draggingRef.current || prefersReducedMotion()) {
      geoRef.current = target;
      draw(target, true);
      return;
    }

    const from = prev;
    // Shorter arc between the two bearings.
    const delta = ((((target.bearing - from.bearing) % 360) + 540) % 360) - 180;
    const t0 = performance.now();
    const DURATION = 350;
    const step = (now: number) => {
      const t = Math.min(1, (now - t0) / DURATION);
      const e = 1 - Math.pow(1 - t, 3);
      if (t >= 1) {
        geoRef.current = target;
        draw(target, true);
        return;
      }
      const cur = {
        r: from.r + (target.r - from.r) * e,
        off: from.off + (target.off - from.off) * e,
        reach: from.reach + (target.reach - from.reach) * e,
        bearing: normaliseBearing(from.bearing + delta * e),
      };
      geoRef.current = { key, ...cur };
      draw(cur, false);
      tweenRef.current = requestAnimationFrame(step);
    };
    tweenRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(tweenRef.current);
  }, [ready, pickedId, pLat, pLng, pRadius, pMode, pBearing, pRunner]);

  // Drag handle: created once per picked start, moved as the bearing changes.
  React.useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    if (pLat == null || pLng == null || pRadius == null || pMode !== "lobe") return;
    const startLngLat: Pt = [pLng, pLat];
    const el = document.createElement("div");
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "wts-handle";
    btn.setAttribute("aria-label", "Drag to swing the area around the " + (cb.current.picked?.start.kind === "station" ? "station" : "car park"));
    btn.innerHTML = SWING_SVG;
    const hint = document.createElement("span");
    hint.className = "wts-hint";
    hint.setAttribute("aria-hidden", "true");
    hint.textContent = "Drag to swing it round";
    hint.style.display = draggedRef.current ? "none" : "";
    el.append(btn, hint);
    hintRef.current = hint;
    handleElRef.current = btn;

    const marker = new maplibregl.Marker({ element: el, anchor: "center" })
      .setLngLat(destination(pLng, pLat, 2 * pRadius, cb.current.picked?.bearing ?? 0))
      .addTo(map);
    handleRef.current = marker;

    const hideHint = () => {
      draggedRef.current = true;
      hint.style.display = "none";
    };
    let dragging = false;
    let last = cb.current.picked?.bearing ?? 0;
    let raf = 0;
    const bearingFromEvent = (e: PointerEvent): number => {
      const rect = map.getCanvas().getBoundingClientRect();
      const sp = map.project(startLngLat);
      const dx = e.clientX - rect.left - sp.x;
      const dy = e.clientY - rect.top - sp.y;
      return normaliseBearing((Math.atan2(dx, -dy) * 180) / Math.PI);
    };
    const onDown = (e: PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragging = true;
      draggingRef.current = true;
      setDragging(true);
      btn.setPointerCapture(e.pointerId);
      map.dragPan.disable();
      hideHint();
    };
    const onMove = (e: PointerEvent) => {
      if (!dragging) return;
      last = bearingFromEvent(e);
      marker.setLngLat(destination(startLngLat[0], startLngLat[1], 2 * pRadius, last));
      if (!raf) {
        raf = requestAnimationFrame(() => {
          raf = 0;
          cb.current.onBearingChange(last);
        });
      }
    };
    const onUp = (e: PointerEvent) => {
      if (!dragging) return;
      dragging = false;
      draggingRef.current = false;
      setDragging(false);
      if (btn.hasPointerCapture(e.pointerId)) btn.releasePointerCapture(e.pointerId);
      map.dragPan.enable();
      cb.current.onBearingChange(last);
      cb.current.onBearingCommit(last);
    };
    const onKey = (e: KeyboardEvent) => {
      const cur = cb.current.picked?.bearing ?? 0;
      let next: number | null = null;
      if (e.key === "ArrowLeft" || e.key === "ArrowDown") next = normaliseBearing(cur - KEY_STEP_DEG);
      else if (e.key === "ArrowRight" || e.key === "ArrowUp") next = normaliseBearing(cur + KEY_STEP_DEG);
      else if (e.key === "Home") next = cb.current.picked?.bestBearing ?? null;
      else if (e.key === "End") next = cb.current.picked?.runnerUpBearing ?? null;
      if (next == null) return;
      e.preventDefault();
      hideHint();
      cb.current.onBearingChange(next);
      cb.current.onBearingCommit(next);
    };
    btn.addEventListener("pointerdown", onDown);
    btn.addEventListener("pointermove", onMove);
    btn.addEventListener("pointerup", onUp);
    btn.addEventListener("pointercancel", onUp);
    btn.addEventListener("keydown", onKey);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      if (dragging) map.dragPan.enable();
      marker.remove();
      handleRef.current = null;
      handleElRef.current = null;
    };
  }, [ready, pLat, pLng, pRadius, pMode, pickedId]);

  // --- start chips, region bubbles, origin ------------------------------
  const showRegions = regions.length > 0 && pickedId == null && zoom < REGION_ZOOM;
  React.useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    const markers: maplibregl.Marker[] = [];
    const add = (el: HTMLElement, lng: number, lat: number, z?: number) => {
      const m = new maplibregl.Marker({ element: el, anchor: "center" }).setLngLat([lng, lat]).addTo(map);
      if (z != null) el.style.zIndex = String(z);
      markers.push(m);
    };

    const inBerth = (lat: number, lng: number) =>
      !origin || haversineM(origin.lat, origin.lng, lat, lng) <= origin.radiusKm * 1000;
    const shownRegions = regions.filter((r) => inBerth(r.lat, r.lng));
    if (showRegions && shownRegions.length > 0) {
      const top = Math.max(...shownRegions.map((r) => r.bestCount));
      for (const r of shownRegions) {
        const b = document.createElement("button");
        b.type = "button";
        b.className = `wts-bub${r.bestCount === top ? " best" : ""}`;
        b.setAttribute("aria-label", `${r.name}: best start reaches ${r.bestCount}, ${r.startCount} good starts. Zoom in.`);
        const pill = document.createElement("span");
        pill.className = "pill";
        pill.setAttribute("aria-hidden", "true");
        pill.textContent = String(r.bestCount);
        const small = document.createElement("small");
        small.textContent = "best start";
        pill.append(small);
        const cap = document.createElement("span");
        cap.className = "cap";
        cap.setAttribute("aria-hidden", "true");
        cap.textContent = `${r.name} · ${r.startCount} good ${r.startCount === 1 ? "start" : "starts"}`;
        b.append(pill, cap);
        b.addEventListener("click", () => map.flyTo({ center: [r.lng, r.lat], zoom: 9.5, duration: prefersReducedMotion() ? 0 : 700 }));
        add(b, r.lng, r.lat);
      }
    } else {
      starts.forEach((s, i) => {
        if (s.id === pickedId || !inBerth(s.lat, s.lng)) return; // drawn separately, with the live count
        // D27: the picked start was folded into this one; one chip per place.
        if (pickedId != null && s.alternatives.some((a) => a.id === pickedId)) return;
        const el = chipEl(s, pickedId == null && i === 0, s.count);
        el.addEventListener("click", () => cb.current.onPickStart(s.id));
        add(el, s.lng, s.lat, pickedId == null && i === 0 ? 3 : 1);
      });
    }
    if (origin) {
      const o = document.createElement("div");
      o.className = "wts-origin";
      o.setAttribute("aria-hidden", "true");
      add(o, origin.lng, origin.lat, 0);
    }
    return () => markers.forEach((m) => m.remove());
  }, [ready, starts, regions, showRegions, pickedId, origin?.lat, origin?.lng, origin?.radiusKm]);

  // The picked start's own chip, in Summit Gold, with the live count.
  React.useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || pLat == null || pLng == null || !cb.current.picked) return;
    const el = chipEl(cb.current.picked.start, true, cb.current.picked.insideCount);
    pickedCountRef.current = el.querySelector(".n");
    const m = new maplibregl.Marker({ element: el, anchor: "center" }).setLngLat([pLng, pLat]).addTo(map);
    m.getElement().style.zIndex = "2";
    return () => {
      m.remove();
      pickedCountRef.current = null;
    };
  }, [ready, pickedId, pLat, pLng]);

  const liveCount = picked?.insideCount;
  React.useEffect(() => {
    if (pickedCountRef.current && liveCount != null) pickedCountRef.current.textContent = String(liveCount);
  }, [liveCount, pickedId]);

  // --- fit ---------------------------------------------------------------
  const lastFit = React.useRef<string | null>(null);
  React.useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !fit.bounds || lastFit.current === fit.key) return;
    lastFit.current = fit.key;
    fitPendingRef.current = true;
    const [w, s, e, n] = fit.bounds;
    map.fitBounds(
      [
        [w, s],
        [e, n],
      ],
      {
        padding: { top: 60, bottom: 80, left: 50, right: 70 },
        duration: prefersReducedMotion() ? 0 : 700,
        maxZoom: 13,
      },
    );
  }, [ready, fit.key, fit.bounds]);

  const showSearchArea = (() => {
    if (!ready || picked != null || dragging || !view) return false;
    if (!origin) return view.zoom >= 8;
    const moved = haversineM(view.lat, view.lng, origin.lat, origin.lng) > (origin.radiusKm * 1000) / 2;
    const zoomed = baseZoomRef.current != null && Math.abs(view.zoom - baseZoomRef.current) > 0.5;
    return moved || zoomed;
  })();

  const searchArea = () => {
    const map = mapRef.current;
    if (!map) return;
    const b = map.getBounds();
    const c = map.getCenter();
    const widthM = haversineM(c.lat, b.getWest(), c.lat, b.getEast());
    const heightM = haversineM(b.getSouth(), c.lng, b.getNorth(), c.lng);
    const radiusKm = Math.min(30, Math.max(2, Math.round((Math.min(widthM, heightM) / 2 / 1000) * 10) / 10));
    baseZoomRef.current = map.getZoom();
    cb.current.onSearchArea({
      lat: Math.round(c.lat * 1000) / 1000,
      lng: Math.round(c.lng * 1000) / 1000,
      radiusKm,
    });
  };

  return (
    <div style={{ position: "relative", width: "100%", height: "100%", background: "var(--surface-sunken)", ...style }}>
      <div ref={containerRef} aria-label="Map of the hills you haven't collected" style={{ position: "absolute", inset: 0 }} />
      {showSearchArea && (
        <button
          type="button"
          onClick={searchArea}
          style={{
            position: "absolute",
            top: 12,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 3,
            height: 38,
            padding: "0 14px",
            display: "flex",
            alignItems: "center",
            gap: 8,
            borderRadius: 999,
            border: "1px solid var(--border-strong)",
            background: "var(--surface-overlay)",
            backdropFilter: "blur(6px)",
            color: "var(--text-primary)",
            font: "600 13px var(--font-sans)",
            cursor: "pointer",
            boxShadow: "var(--shadow-md)",
          }}
        >
          <ScanSearch size={16} aria-hidden="true" />
          Search this area
        </button>
      )}
      {children}
    </div>
  );
}
